import React, { useEffect, useState } from 'react'
import './DataTable.scss'
import PropTypes from 'prop-types'
import { formatNumber } from '@engine/utils/format'
import { DataTableRow } from '@components/common'
import { Button, Checkbox } from '@material-ui/core'
import { Add, Delete, Undo } from '@material-ui/icons'
import { makeStyles } from '@material-ui/core/styles'
import { uuid } from 'uuidv4'
import FormControlLabel from '@material-ui/core/FormControlLabel'
import EditIcon from '@material-ui/icons/Edit'

const useStyles = makeStyles(() => ({
  root: {
    border: '1px solid #9e9e9e',
    borderRadius: '5px'
  },
  iconStyle: {
    color: '#757575',
    fontSize: '1.4rem',
    marginRight: '3px'
  },
  editPen: {
    fontSize: '14px',
    position: 'relative',
    top: '1px'
  }
}))

export const DataTable = props => {
  const classes = useStyles()
  const {
    dataTableColumns,
    rowsCount,
    onDragOver,
    onDragStart,
    onSelect,
    handleDeleteRow,
    handleDeleteSelected,
    handleUpdateRow, // onUpdate,
    handleReset,
    onDrop,
    handleChange,
    actionItems,
    tableData,
    confirmAddRow,
    editRowIndex,
    modifyEditRowIndex,
    handleSetRowInEditFlag,
    onHideToggle,
    hideUnitMixTotal,
    unitMixDisclaimerData
  } = props
  const {
    localComparableData,
    checked,
    selectedRows = [],
    isLastRec,
    reset
  } = tableData
  const {
    disclaimerText,
    showDisclaimerForm
  } = unitMixDisclaimerData
  // const [editRowIndex, setEditRowIndex] = useState(-1)
  const [initTableData, setInitTableData] = useState(localComparableData)
  const [newRowFlag, setNewRowFlag] = useState(false)
  const [noDataValidation, setNoDataValidation] = useState(false)
  const [hideTotal, setHideTotal] = useState(hideUnitMixTotal || false)

  useEffect(
    () => {
      setInitTableData([...localComparableData])
    },
    [localComparableData]
  )
  useEffect(
    () => {
      setHideTotal(hideUnitMixTotal)
    },
    [hideUnitMixTotal]
  )

  const dataAttributes = dataTableColumns
    ?.filter(prop => prop.value !== 'NA')
    .map(prop => prop.value)
  const totalRow = { ...initTableData[initTableData.length - 1] }
  const { hasSelect, hasAdd } = actionItems ? actionItems : {}
  const handleAddRow = () => {
    const newRow = {
      date: null,
      unitEffectiveRent: null,
      comments: 'NA',
      totalMr: null,
      unitSize: null,
      unitMarketRent: null,
      source: 'BCM',
      units: null,
      mrPerSf: null,
      unitType: '',
      totalSize: null,
      erPerSf: null,
      totalEr: null,
      compsRentId: uuid(),
      omniViewPropertyRentId: '',
      beds: null,
      erPerBed: null
    }
    const tempCData = [...initTableData]
    if (tempCData.length) {
      tempCData.splice(tempCData.length - 1, 0, newRow)
      setInitTableData([...tempCData])
      modifyEditRowIndex(tempCData.length - 2)
      setNewRowFlag(true)
    } else {
      tempCData.splice(1, 0, newRow)
      tempCData.splice(1, 0, newRow)
      setInitTableData([...tempCData])
      modifyEditRowIndex(0)
      setNewRowFlag(true)
    }
    handleSetRowInEditFlag(true)
  }

  const cancelAddRow = () => {
    const tempCData = [...initTableData]
    tempCData.splice(tempCData.length - 2, 1)
    setInitTableData([...tempCData])
    setNewRowFlag(false)
    modifyEditRowIndex(-1)
    setNoDataValidation(false)
    handleSetRowInEditFlag(false)
  }

  const emptyRowHandler = val => {
    setNoDataValidation(val)
  }

  const handleHideToggle = e => {
    const checkedToggle = e.target.checked
    const hideTotalVal = hideTotal
    setHideTotal(!hideTotalVal)
    onHideToggle(checkedToggle)
  }

  return (
    <>
      <div className="data-table-container" data-testid="data-table-container">
        <div className="edit-data-table">
          <div className="table-buttons">
            {hasAdd && (
              <Button
                className={classes.root}
                id="add-row"
                disabled={editRowIndex !== -1 || selectedRows.length >= 1}
                data-testid="add-row"
                variant="contained"
                size="small"
                onClick={handleAddRow}
              >
                <span className="add-row">
                  <Add className={classes.iconStyle} />
                  <div>Add Row</div>
                </span>
              </Button>
            )}
            {hasSelect && (
              <>
                <Button
                  className={classes.root}
                  id="delete-selected"
                  disabled={selectedRows.length === 0}
                  data-testid="delete-selected"
                  variant="contained"
                  size="small"
                  onClick={handleDeleteSelected}
                >
                  <Delete className={classes.iconStyle} />
                  <span className="reset">Delete Selected Rows</span>
                </Button>
                {!showDisclaimerForm && (
                  <div className="btn-table-reset">
                    <Button
                      className={classes.root}
                      id="delete-reset"
                      disabled={!reset || editRowIndex !== -1}
                      data-testid="delete-reset"
                      variant="contained"
                      size="small"
                      onClick={handleReset}
                    >
                      <Undo className={classes.iconStyle} />
                      <span className="reset">Reset</span>
                    </Button>
                  </div>
                )}
              </>
            )}
          </div>
          <div className="edit-data-headers">
            <div className="global-checkbox">
              {hasSelect && (
                <Checkbox
                  checked={checked}
                  onChange={handleChange}
                  color="primary"
                  disabled={editRowIndex !== -1}
                  inputProps={{
                    'aria-label': 'secondary checkbox',
                    'data-testid': 'data-table-row-all'
                  }}
                />
              )}
            </div>
            {dataTableColumns.map((obj, index) =>
            (obj.key !== 'NA' ? (
              <div key={index} className="header-text">
                {obj.key}
              </div>
            ) : null)
            )}
          </div>
          <div className="data-table-rows">
            {initTableData &&
              initTableData
                .slice(0, initTableData.length - 1)
                .map((row, index) => (
                  <>
                    {!row.isRemoved &&
                      <DataTableRow
                        editRowIndex={editRowIndex}
                        onSelect={onSelect}
                        selectedRows={selectedRows}
                        key={index}
                        row={row}
                        index={index}
                        onDragOver={onDragOver}
                        onDragStart={onDragStart}
                        onDrop={onDrop}
                        comparableData={initTableData}
                        rowsCount={rowsCount}
                        dataAttributes={dataAttributes}
                        onEdit={modifyEditRowIndex}
                        onDelete={handleDeleteRow}
                        onUpdate={handleUpdateRow}
                        isLastRec={isLastRec}
                        actionItems={actionItems}
                        newRowFlag={newRowFlag}
                        confirmAddRow={newRow => {
                          setNewRowFlag(false)
                          modifyEditRowIndex(-1)
                          confirmAddRow(newRow)
                        }}
                        cancelAddRow={cancelAddRow}
                        onEmptyRow={emptyRowHandler}
                      />
                    }
                  </>
                ))}
          </div>
          <div className="total-sticky">
            <div className="hidden-div" />
            {!hideTotal && dataAttributes.map((key, idx) => (
              <div key={idx} className="data-text">
                {formatNumber(key, totalRow[key])}
              </div>
            ))}
            {hideTotal && (
              <div className="data-text-hidden"></div>
            )}
            <div className="hideToggleBox">
              <FormControlLabel
                control={
                  <Checkbox
                    color="primary"
                    checked={hideTotal}
                    onChange={handleHideToggle}
                    inputProps={{
                      'aria-label': 'secondary checkbox',
                      'data-testid': 'hide-totals'
                    }}
                  />
                }
                label="Hide Totals"
              />
            </div>
          </div>
          <div className="table_disclaimer_text">
            <span className="add_disclaimer_text">{(disclaimerText && disclaimerText.trim()) ? disclaimerText : '+ Add Disclaimer text'}</span>
            <div
              onClick={props.handleDisclaimerEdit}
              className="add_disclaimer_edit"
            >
              <EditIcon className={classes.editPen} />
            </div>
          </div>
        </div>
      </div>
      {!showDisclaimerForm && (
        <div className="rows-count">
          <div className="count-info">
            {selectedRows.length}/{initTableData.length - 1} rows
            {noDataValidation && (
              <span className="warning_txt">
                Please fill atleast one field to add new unit mix data.
              </span>
            )}
          </div>
          <small className="detail-info">
            * First {rowsCount} rows will be considered for Weighted Avg.
          </small>
        </div>
      )}
    </>
  )
}

DataTable.propTypes = {
  tableData: PropTypes.shape({
    localComparableData: PropTypes.array,
    checked: PropTypes.bool,
    dragIndex: PropTypes.number,
    reset: PropTypes.bool,
    selectedRows: PropTypes.array,
    isLastRec: PropTypes.bool
  }),
  propertyType: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  dataTableColumns: PropTypes.array,
  comparableData: PropTypes.array,
  rowsCount: PropTypes.number,
  onDragOver: PropTypes.func,
  onDragStart: PropTypes.func,
  onDrop: PropTypes.func,
  onSelect: PropTypes.func,
  handleDeleteRow: PropTypes.func,
  handleDeleteSelected: PropTypes.func,
  handleUpdateRow: PropTypes.func,
  handleChange: PropTypes.func,
  handleReset: PropTypes.func,
  confirmAddRow: PropTypes.func,
  editRowIndex: PropTypes.number,
  modifyEditRowIndex: PropTypes.func,
  actionItems: PropTypes.shape({
    hasDelete: PropTypes.bool,
    hasDragDrop: PropTypes.bool,
    hasEdit: PropTypes.bool,
    hasSelect: PropTypes.bool
  }),
  onHideToggle: PropTypes.func,
  hideUnitMixTotal: PropTypes.bool,
  handleSetRowInEditFlag: PropTypes.func,
  handleDisclaimerEdit: PropTypes.func,
  unitMixDisclaimerData: PropTypes.shape({
    disclaimerText: PropTypes.string,
    showDisclaimerForm: PropTypes.bool
  })
}

export default DataTable
